import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, Observable, Subject, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, takeWhile, throttleTime } from 'rxjs/operators';

export enum SessionTimeoutState {
  Active = 'Active',
  Warning = 'Warning',
  TimedOut = 'TimedOut',
  Disabled = 'Disabled',
}

export interface SessionTimeoutConfig {
  timeoutMinutes?: number;
  warningSeconds?: number;
}

const DEFAULT_TIMEOUT_MINUTES = 30;
const DEFAULT_WARNING_SECONDS = 120;
const ACTIVITY_THROTTLE_MS = 200;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private readonly stateSubject = new BehaviorSubject<SessionTimeoutState>(SessionTimeoutState.Disabled);
  private readonly countdownSubject = new BehaviorSubject<number>(0);
  private readonly timeoutSubject = new Subject<void>();
  private readonly idleStartSubject = new Subject<void>();
  private readonly interruptSubject = new Subject<void>();

  readonly state$ = this.stateSubject.asObservable().pipe(distinctUntilChanged());
  readonly countdown$: Observable<number> = this.countdownSubject.asObservable();
  readonly timeout$: Observable<void> = this.timeoutSubject.asObservable();
  readonly idleStart$: Observable<void> = this.idleStartSubject.asObservable();
  readonly interrupt$: Observable<void> = this.interruptSubject.asObservable();

  private pipelineSubscription: Subscription | null = null;
  private warningSeconds = DEFAULT_WARNING_SECONDS;
  private inWarning = false;

  constructor(private readonly ngZone: NgZone) {}

  start(config: SessionTimeoutConfig): void {
    this.stop();

    const timeoutMinutes = config.timeoutMinutes ?? DEFAULT_TIMEOUT_MINUTES;

    if (timeoutMinutes <= -1) {
      this.stateSubject.next(SessionTimeoutState.Disabled);
      return;
    }

    this.warningSeconds = config.warningSeconds ?? DEFAULT_WARNING_SECONDS;
    // Idle period = total timeout minus the warning countdown
    const idleMs = timeoutMinutes * 60 * 1000 - this.warningSeconds * 1000;

    this.stateSubject.next(SessionTimeoutState.Active);
    this.inWarning = false;

    // Create activity stream outside Angular zone to avoid change detection on every event
    let activity$!: Observable<unknown>;
    this.ngZone.runOutsideAngular(() => {
      const eventStreams = ACTIVITY_EVENTS.map((event) => fromEvent(document, event));
      activity$ = merge(...eventStreams).pipe(throttleTime(ACTIVITY_THROTTLE_MS));
    });

    this.pipelineSubscription = activity$
      .pipe(
        startWith(null), // Start the idle timer immediately
        switchMap(() => {
          // If we were in warning phase, this activity is an interrupt
          if (this.inWarning) {
            this.inWarning = false;
            this.ngZone.run(() => {
              this.stateSubject.next(SessionTimeoutState.Active);
              this.interruptSubject.next();
            });
          }

          // Wait for idle period to expire
          return timer(idleMs).pipe(
            switchMap(() => {
              // Idle period expired — start warning countdown
              this.inWarning = true;
              this.ngZone.run(() => {
                this.stateSubject.next(SessionTimeoutState.Warning);
                this.countdownSubject.next(this.warningSeconds);
                this.idleStartSubject.next();
              });

              // Tick every second: warningSeconds-1 down to 0, then stop
              return timer(1000, 1000).pipe(
                map((tick) => this.warningSeconds - 1 - tick),
                takeWhile((remaining) => remaining > 0, true),
                map((remaining) => {
                  this.ngZone.run(() => {
                    this.countdownSubject.next(remaining);
                  });

                  if (remaining <= 0) {
                    this.ngZone.run(() => {
                      this.stateSubject.next(SessionTimeoutState.TimedOut);
                      this.timeoutSubject.next();
                    });
                  }

                  return remaining;
                })
              );
            })
          );
        })
      )
      .subscribe();
  }

  stop(): void {
    this.pipelineSubscription?.unsubscribe();
    this.pipelineSubscription = null;
    this.inWarning = false;
    this.stateSubject.next(SessionTimeoutState.Disabled);
  }

  getWarningSeconds(): number {
    return this.warningSeconds;
  }
}
